import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { Text, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import EditorCloseIcon from '@atlaskit/icon/core/close';
import { IconButton } from '@atlaskit/button/new';

type UnpublishedStateEditPermsBannerProps = {
	handleClose: () => void;
};

const i18n = defineMessages({
	bannerMessage: {
		id: 'company-hub.edit-perms-banner.message',
		defaultMessage:
			'<b>Only you and other hub editors can view and make changes.</b> When you’re ready, select <b>Publish</b> to make the hub visible to all.',
		description:
			'This is the message on the Company Hub edit perms banner, which is shown only to first-time hub editors and will persist until they first publish a hub.',
	},
	bannerClose: {
		id: 'company-hub.edit-perms-banner.close',
		defaultMessage: 'Close',
		description: 'Label for the close icon. Clicking this icon closes the edit perms banner',
	},
});

const textContainerStyles = xcss({
	gap: 'space.100',
});

const bannerContainerStyles = xcss({
	width: '100%',
	padding: 'space.200',
	background: `linear-gradient(84.09deg,
  ${token('color.background.accent.blue.subtlest')} 0%,
  ${token('color.background.accent.purple.subtlest')} 100%)`,
});

export const UnpublishedStateEditPermsBanner = ({
	handleClose,
}: UnpublishedStateEditPermsBannerProps) => {
	const { formatMessage } = useIntl();

	return (
		<Flex justifyContent="space-between" xcss={bannerContainerStyles}>
			<Flex alignItems="center" xcss={textContainerStyles}>
				<OfficeBuildingIcon spacing="spacious" label="" color={token('color.icon')} />
				<Text>
					<FormattedMessage
						{...i18n.bannerMessage}
						values={{
							b: (...chunks) => <b>{chunks}</b>,
						}}
					/>
				</Text>
			</Flex>
			<IconButton
				appearance="subtle"
				spacing="compact"
				onClick={handleClose}
				label={formatMessage(i18n.bannerClose)}
				icon={(iconProps) => <EditorCloseIcon {...iconProps} color={token('color.icon')} />}
			/>
		</Flex>
	);
};
